import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {IconButton} from 'platform/components';
import {Icon} from 'platform/foundation';
import styled from 'styled-components';

import {useMemo} from 'react';

import {join} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {GetTrimLevelsApiResponse, RelativeFilterResponseBody} from '../../../types/api/api';
import {FilterFormData} from '../../../types/FilterFormData';
import {PriceReportType} from '../../../types/PriceReportType';
import {PresetBadges} from '../../Filter/components/SavedFilters/components/PresetBadges';
import {getPresetDisplayName} from '../../Filter/components/SavedFilters/utils/getPresetDisplayName';
import type {IsKnownPresetFeature} from '../../Filter/components/SavedFilters/utils/normalizePresetFeatures';
import {formValuesToRequestValues} from '../../Filter/utils/formValuesToRequestValues';
import {useMarketMetrics} from '../hooks/useMarketMetrics';
import {useStaggeredQueryArg} from '../hooks/useStaggeredQueryArg';
import {buildFilterSummary, FilterSummaryHelpers} from '../utils/buildFilterSummary';
import {resolvePresetFormValues, toMetricsRequestBody} from '../utils/buildMetricsRequestBody';
import {PANEL_STACKED_MEDIA, PanelGridRow} from './PanelGrid';
import {PresetMetricsCells} from './PresetMetricsCells';

interface PresetMetricsRowProps extends TestIdProps {
  preset: RelativeFilterResponseBody;
  priceReport: PriceReportType;
  /** Form snapshot taken when the panel was expanded — the “what if selected” baseline. */
  baseFormValues: FilterFormData;
  trimLevelsCatalog: GetTrimLevelsApiResponse['trim_levels'] | null;
  isKnownFeature: IsKnownPresetFeature;
  summaryHelpers: FilterSummaryHelpers;
  formatPrice: (price: number | null) => string;
  /** Primary advertisement server the position is measured against. */
  primaryServer?: string;
  /** The vehicle's own listing price when already advertised. */
  advertisedPrice?: number | null;
  /** Per-row query start offset — the expanded panel ramps its requests up. */
  queryStartDelayMs?: number;
  isActive: boolean;
  isSortable: boolean;
  onSelect: (preset: RelativeFilterResponseBody) => void;
  /** Opens the preset editor for this row. */
  onEdit: (preset: RelativeFilterResponseBody) => void;
}

/**
 * One expanded-panel row: metrics the market would show if this preset were selected.
 * Mounted lazily (only while the panel is expanded), so each row fetches its own
 * statistics on first expand and serves from the RTK Query cache afterwards.
 */
export function PresetMetricsRow(props: PresetMetricsRowProps) {
  const {preset, priceReport, baseFormValues, trimLevelsCatalog, isKnownFeature} = props;

  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: preset.id,
    disabled: !props.isSortable,
  });

  const plainBody = useMemo(
    () =>
      formValuesToRequestValues(
        resolvePresetFormValues(
          preset.config,
          priceReport,
          baseFormValues,
          trimLevelsCatalog,
          isKnownFeature
        )
      ),
    [preset.config, priceReport, baseFormValues, trimLevelsCatalog, isKnownFeature]
  );

  const queryArg = useMemo(
    () => ({
      reportId: priceReport.id,
      body: toMetricsRequestBody(plainBody, priceReport.sellingPrice.recommended.value ?? null),
    }),
    [priceReport, plainBody]
  );
  const staggeredQueryArg = useStaggeredQueryArg(queryArg, props.queryStartDelayMs ?? 0);
  const isDeferred = staggeredQueryArg !== queryArg;
  const {metrics, isLoading, isError, retry} = useMarketMetrics(staggeredQueryArg, priceReport);

  // An errored row drops its button semantics — the retry Link is the only action
  // and nesting an interactive Link inside a role="button" row is an a11y issue.
  // Mouse clicks elsewhere in the row still select the preset.
  const isRowInteractive = !isError;

  const description = useMemo(
    () => join(', ', buildFilterSummary(plainBody, props.summaryHelpers)),
    [plainBody, props.summaryHelpers]
  );

  return (
    <PanelGridRow
      ref={setNodeRef}
      role={isRowInteractive ? 'button' : undefined}
      tabIndex={isRowInteractive ? 0 : undefined}
      $isActive={props.isActive}
      $isDragging={isDragging}
      style={{
        transform: CSS.Transform.toString(transform) ?? undefined,
        transition: transition ?? undefined,
      }}
      onClick={() => props.onSelect(preset)}
      onKeyDown={
        isRowInteractive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                props.onSelect(preset);
              }
            }
          : undefined
      }
      data-testid={suffixTestId(`presetRow-${preset.id}`, props)}
    >
      <PresetMetricsCells
        name={getPresetDisplayName(preset)}
        nameBadge={<PresetBadges preset={preset} data-testid={props['data-testid']} />}
        leadingSlot={
          props.isSortable ? (
            <DragHandle
              {...attributes}
              {...listeners}
              onClick={(event) => event.stopPropagation()}
              data-testid={suffixTestId(`dragHandle-${preset.id}`, props)}
            >
              <Icon value="action/drag_indicator" color="palettes.neutral.60.100" size={4} />
            </DragHandle>
          ) : (
            <DragHandleSpacer />
          )
        }
        description={description}
        metrics={metrics}
        isLoading={isLoading || isDeferred}
        isError={isError}
        onRetry={retry}
        primaryServer={props.primaryServer}
        advertisedPrice={props.advertisedPrice}
        formatPrice={props.formatPrice}
        data-testid={suffixTestId(`presetRow-${preset.id}`, props)}
      />
      <RowActions onClick={(event) => event.stopPropagation()}>
        <IconButton
          icon="action/settings"
          priority="tertiary"
          onClick={() => props.onEdit(preset)}
          aria-label={i18n.t('entity.filterForm.savedFilters.editPreset')}
          data-testid={suffixTestId(`editPreset-${preset.id}`, props)}
        />
      </RowActions>
    </PanelGridRow>
  );
}

const RowActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  /* Stacked layout: the gear sits next to the preset name in the first row */
  @media ${PANEL_STACKED_MEDIA} {
    grid-row: 1;
    grid-column: 2;
    align-self: start;
  }
`;

/* Drag-to-sort is a desktop affordance — hidden on touch layouts. */
const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({theme}) => theme.getSize(4)};
  flex-shrink: 0;
  cursor: grab;
  touch-action: none;

  @media ${PANEL_STACKED_MEDIA} {
    display: none;
  }
`;

const DragHandleSpacer = styled.div`
  width: ${({theme}) => theme.getSize(4)};
  flex-shrink: 0;

  @media ${PANEL_STACKED_MEDIA} {
    display: none;
  }
`;
